<template>
  <div class="organization-responsibilities-table pt-4">
    <SearchBar
      v-if="showSearchBar"
      type="button"
      ghostText="Search Responsibility"
      :add="true"
      class="mb-5 mx-4"
      :label="appliedFilters.length > 0 ? 'More Filters' : 'Filter'"
      :length="appliedFilters.length"
      :chipText="appliedFilters"
      actionButtonIcon="add-icon.svg"
      btnText="Add Responsibility"
      @onClick="emitCorrectOnClick($event)"
      @onSearch="handleSearch($event)"
      @removeFilter="removeFilter"
      :valueSearch="searchText"
    />
    <SearchModal
      ref="searchModal"
      :dialog="showFilterDialog"
      @back="showFilterDialog = false"
      @checkedItemsChanged="filterResponsibilities"
      :clearSelection="clearSelection"
      filterType="Responsibilities"
      :filterGroups="filterGroups"
      :statusOptions="statusOptions"
      :modalWidth="420"
      :defaultSelected="defaultSelected"
    />
    <DxTreeList
      v-if="responsibilities.length > 0"
      :ref="el => treelist = el"
      id="responsibilities-treelist"
      :data-source="responsibilities"
      :show-row-lines="false"
      key-expr="id"
      :autoExpandAll="false"
      parent-id-expr="parentId"
      has-items-expr="has_items"
      :auto-navigate-to-focused-row="false"
      :root-value="-1"
      :scrolling="{
        mode: 'standard',
        useNative: true,
        scrollByContent: true,
        scrollByThumb: false,
        showScrollbar: 'never'
      }"
      height="auto"
      @rowPrepared="onRowPrepared"
    >
      <!-- Responsibility Column -->
      <DxColumn
        data-field="data"
        caption="Responsibility"
        cell-template="responsibilityCell"
        header-cell-template="responsibilityHeader"
        :allowSearch="false"
        :allowSorting="false"
      />
      <template #responsibilityHeader>
        <div class="sortable-header-container">
          <div class="d-flex align-center sortable-header-content">
            <span>Responsibility</span>
            <v-icon 
              v-if="sortField === 'data'" 
              class="sort-icon ml-1"
              color="#7f56d9"
              small
            >
              {{ getSortIcon() }}
            </v-icon>
          </div>
        </div>
      </template>
      <template #responsibilityCell="{ data: options }">
        <div class="d-flex org-object-wrapper">
          <OrgObjectCard
            :codeValue="getOrgCodeName(options)"
            :name="options.data.data.name"
            :type="'responsibility'"
            :state="getCardState(options.data.status)"
            :icon="true"
            :code="true"
            :size="'Medium'"
            @openPopup="handleResponsibilityClick(options.data)"
            :showDisabledHover="false"
            :showTooltip="true"
          />
        </div>
      </template>

      <!-- Attached To Column -->
      <DxColumn
        caption="Attached To"
        cell-template="attachedToCell"
        header-cell-template="attachedToHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="105"
      />
      <template #attachedToHeader>
        <div class="d-flex justify-center full-width align-center">
          <Icon class="mr-1" name="org_icon_attach" size="small" />
          <span>Attached To</span>
        </div>
      </template>
      <template #attachedToCell="{ data: options }">
      <div class="d-flex attachments-counter-cell-wrapper" v-if="options.data.count_of_attachments_to_roles > 0" @click="openAttachmentModal(options.data)">
        <AttachmentsCounter
            perspective="responsibility"
            :count_of_attachments_to_roles="options.data.count_of_attachments_to_roles || 0"
          />
        </div>
      </template>

      <!-- Checkpoints Column -->
      <DxColumn
        caption="Checkpoints"
        cell-template="checkpointsCell"
        header-cell-template="checkpointsHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="160"
      />
      <template #checkpointsHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Checklist</span>
        </div>
      </template>
      <template #checkpointsCell="{ data: options }">
        <div class="d-flex checkpoints-cell-wrapper">
          <CheckpointCounters
            :apprentice_active="options.data.apprentice_active || 0"
            :apprentice_draft="options.data.apprentice_draft || 0"
            :professional_active="options.data.professional_active || 0"
            :professional_draft="options.data.professional_draft || 0"
            :coach_active="options.data.coach_active || 0"
            :coach_draft="options.data.coach_draft || 0"
            :master_active="options.data.master_active || 0"
            :master_draft="options.data.master_draft || 0"
            @onClick="handleChecklistClick(options.data)"
          />
        </div>
      </template>

      <!-- Low Level Assignments Column -->
      <DxColumn
        caption="Assignments Readiness"
        cell-template="assignmentsCell"
        header-cell-template="assignmentsHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="150"
      />
      <template #assignmentsHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Assignments Readiness</span>
        </div>
      </template>
      <template #assignmentsCell="{ data: options }">
        <div class="d-flex assignments-cell-wrapper">
          <LowLevelAssignments
            :countOfNoviceAssigments="options.data.countOfNoviceAssigments || 0"
            :countOfApprenticeAssigments="options.data.countOfApprenticeAssigments || 0"
            :countOfProfessionalAssignments="options.data.countOfProfessionalAssignments || 0"
            @onClickAssignments="handleAssignmentsClick(options.data)"
          />
        </div>
      </template>

      <!-- People Column -->
      <DxColumn
        caption="Coaches"
        cell-template="peopleCell"
        header-cell-template="coachesHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="138"
      />
      <template #coachesHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Coaches</span>
        </div>
      </template>
      <template #peopleCell="{ data: options }">
        <div class="d-flex people-cell-wrapper">
          <PeopleAvatarsButton
            :avatars="(options.data.data && options.data.data.people) ? options.data.data.people : []"
            :maxVisible="3"
            size="medium"
            :isHoverEnable="true"
            :isShowTooltip="true"
            :allowDrillDown="true"
            @onAvatarClick="handleCoachesClick(options.data)"
          />
        </div>
      </template>

      <!-- Masters Column -->
      <DxColumn
        caption="Masters"
        cell-template="mastersCell"
        header-cell-template="mastersHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="138"
      />
      <template #mastersHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Masters</span>
        </div>
      </template>
      <template #mastersCell="{ data: options }">
        <div class="d-flex masters-cell-wrapper">
          <PeopleAvatarsButton
            :avatars="(options.data.data && options.data.data.masters) ? options.data.data.masters : []"
            :maxVisible="3"
            size="medium"
            :isHoverEnable="true"
            :isShowTooltip="true"
            :allowDrillDown="true"
            @onAvatarClick="handleMastersClick(options.data)"
          />
        </div>
      </template>
      
      <!-- Status Column -->
      <DxColumn
        caption="Status"
        cell-template="statusCell"
        header-cell-template="statusHeader"
        :allowSearch="false"
        :allowSorting="false"
        :width="78"
      />
      <template #statusHeader>
        <div class="d-flex justify-center full-width align-center">
          <span>Status</span>
        </div>
      </template>
      <template #statusCell="{ data: options }">
        <div class="d-flex status-cell-wrapper">
          <OrgObjectStatusBadge
            :status="options.data.status || 'inactive'"
            :archived="options.data.archived || false"
          />
        </div>
      </template>

      <!-- Chatter Column -->
      <DxColumn
        caption=""
        cell-template="chatterCell"
        :allowSearch="false"
        :allowSorting="false"
        :width="50"
      />
      <template #chatterCell="{ data: options }">
        <div 
          class="chatter-clickable"
          @click="openChatter(options.data)"
        >
          <ChatterIcon />
        </div>
      </template>

      <!-- Menu Column -->
      <DxColumn
        caption=""
        cell-template="menuCell"
        :allowSearch="false"
        :allowSorting="false"
        :width="50"
      />
      <template #menuCell="{ data: options }">
        <div class="hide-left-border center-vertical menu-cell-wrapper" @click.stop>
          <ResponsibilityLineMenu
            :key="`menu-${options.data.id}`"
            :status="getResponsibilityStatus(options.data)"
            :responsibilityName="getResponsibilityName(options.data)"
            @action="handleResponsibilityAction($event, options.data)"
          />
        </div>
      </template>
    </DxTreeList>

    <!-- Pagination -->
    <ExPagination
      v-if="responsibilities.length > 0"
      :pagination="pagination"
      @onRequest="handlePaginationRequest"
    />
    
    <!-- Empty State: No records exist at all -->
    <NoRecordFound
      v-else-if="totalCount === 0 && !showSearchBar"
      title="Responsibilities"
      message="responsibilities"
      btnTitle="Responsibility"
      :showActiveCounter="false"
      :noBorder="true"
      showSpecificMessage="No responsibilities exist"
      description="To manage readiness, employees need to be assigned to positions to inherit roles and responsibilities. Add at least one responsibility."
      @onAction="$emit('add-responsibility')"
    />

    <!-- Empty State: Records exist but filtered out -->
    <NoRecordFound
      v-else-if="showSearchBar"
      title="Responsibilities"
      message="responsibilities"
      btnTitle="Responsibility"
      :showActiveCounter="false"
      :noBorder="true"
      :showClearAction="true"
      :shouldShowAddAction="false"
      :clearLabel="clearBtnLabel"
      :description="clearDescription"
      @onClear="$emit('clear-search')"
    />

    <!-- Attachments Modal -->
    <DetachOrgObjectsDialog
      :dialog="showAttachments"
      @cancel="showAttachments = false"
      :orgObjectItems="attachItems"
      :objectType="objectValues.objectType"
      :objectCode="objectValues.objectCode"
      :objectName="objectValues.objectName"
      @detach="detachItems($event)"
      :loader="props.dLoader"
    />

    <!-- Chatter Drawer -->
    <ChatterDrawer
      ref="drawerContainer"
      :drawer="drawer"
      :iconName="iconName"
      :type="typeName"
      :typeValue="typeValue"
      :listData="listData"
      @closeDrawer="closeDrawer($event)"
      :chatterList="props.chatterData"
      :totalCheckpointChatter="props.totalChatterData"
      @requestMoreCheckpointsChatter="emit('requestMoreCheckpointsChatter', $event)"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from "vue";
import { DxTreeList, DxColumn } from "devextreme-vue/tree-list";
import OrgObjectCard from "@components/OrgObjectCard/OrgObjectCard.vue";
import AttachmentsCounter from "@components/AttachmentsCounter/AttachmentsCounter.vue";
import CheckpointCounters from "@components/CheckpointCountersButton/CheckpointCountersButton.vue";
import LowLevelAssignments from "@components/LowLevelAssignments/LowLevelAssignments.vue";
import PeopleAvatarsButton from "@components/PeopleAvatarsButton/PeopleAvatarsButton.vue";
import Icon from "@components/icons/Icon.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import ChatterDrawer from "@components/Drawer/ChatterDrawer.vue";
import ResponsibilityLineMenu from "@components/ResponsibilityLineMenu/ResponsibilityLineMenu.vue";
import SearchBar from "@components/SearchBar/SearchBar.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import DetachOrgObjectsDialog from "@components/DetachOrgObjectsDialog/DetachOrgObjectsDialog.vue";
import { useHighlightRow } from "@/composables/common/useHighlightRow.js";
import { getResponsibilityAttachments } from "@/api";

const props = defineProps({
  responsibilities: {
    type: Array,
    default: () => [],
  },
  allResponsibilities: {
    type: Array,
    default: () => [],
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      total: 0,
      perPage: 10,
    }),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  chatterData: {
    type: Array,
    default: () => [],
  },
  totalChatterData: {
    type: Number,
    default: 0,
  },
  searchText: {
    type: String,
    default: "",
  },
  appliedFilters: {
    type: Array,
    default: () => ["draft", "active"],
  },
  filterGroups: {
    type: Array,
    default: () => [],
  },
  statusOptions: {
    type: Array,
    default: () => ["draft", "active", "retired", "inactive"],
  },
  defaultSelected: {
    type: Array,
    default: () => ["draft", "active"],
  },
  showSearchBar: {
    type: Boolean,
    default: true,
  },
  clearBtnLabel: {
    type: String,
    default: "",
  },
  clearDescription: {
    type: String,
    default: "",
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  dLoader: {
    type: Boolean,
    default: false,
  },
});

const sortField = ref(null);
const sortOrder = ref("asc");
const showFilterDialog = ref(false);
const clearSelection = ref("");
const treelist = ref(null);

// Attachment modal state
const showAttachments = ref(false);
const attachItems = ref([]);
const objectValues = ref({});

// Chatter drawer state
const drawer = ref(false);
const listData = ref({});
const typeName = ref("");
const iconName = ref("");
const typeValue = ref("");
const drawerContainer = ref(null);

// Setup highlight row listener
const { listen, remove } = useHighlightRow();

// Get proxy for accessing $loader and router
const instance = getCurrentInstance();
const proxy = instance?.proxy;
const router = proxy?.$router;

// Debounce utility function
let searchTimeout = null;
const debounce = (func, wait) => {
  return (...args) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    searchTimeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

// Highlight row by index
const highlightRow = (index = 0) => {
  if (!treelist.value?.instance) return;
  const treeListInstance = treelist.value.instance;
  const rowsArray = treeListInstance.getRowElement(index);
  if (rowsArray && rowsArray[0]) {
    const targetRow = rowsArray[0];
    targetRow.style.transition = 'all 1s ease';
    targetRow.style.transform = 'translateY(8px)';
    if (targetRow.classList.contains("org-object-tree-normal-row-background")) {
      targetRow.classList.remove("org-object-tree-normal-row-background");
    }

    const scrollTop = targetRow.getBoundingClientRect().top;
    if (scrollTop) {
      const mainContainer = document.querySelector('.ex-main-container');
      if (mainContainer) {
        const elementRect = targetRow.getBoundingClientRect();
        const containerRect = mainContainer.getBoundingClientRect();
        const scrollPosition = elementRect.top - containerRect.top + mainContainer.scrollTop;

        mainContainer.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }

    targetRow.classList.add("org-object-tree-highlighted-row");

    setTimeout(() => {
      targetRow.style.transform = 'none';
      if (targetRow.classList.contains("org-object-tree-highlighted-row")) {
        targetRow.classList.remove("org-object-tree-highlighted-row");
      }
      targetRow.classList.add("org-object-tree-normal-row-background");
    }, 1000);
  }
};

// Highlight event handler
const handleHighlightEvent = (event) => {
  if (event) {
    setTimeout(() => {
      nextTick(() => {
        const rowIndex = props.responsibilities.findIndex(el => el.id == event.id || el.realId == event.id);
        if (rowIndex > -1) {
          highlightRow(rowIndex);
        }
      });
    }, 1000);
  }
};

// Listen for Responsibilities highlight events
onMounted(() => {
  listen("Responsibilities", handleHighlightEvent);
});

onBeforeUnmount(() => {
  remove("Responsibilities", handleHighlightEvent);
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
});

const getOrgCodeName = (options) => {
  if (options?.data?.info?.code_str) {
    return options?.data?.info?.code_str.split(" ")[0];
  } else if (options?.data?.data?.code_str) {
    return options?.data?.data?.code_str.split(" ")[0];
  } else if (options?.data?.data?.code) {
    return "#" + options.data.data.code;
  }
  return "#0000";
};

const onRowPrepared = (e) => {
  if (e.rowType === "data") {
    e.rowElement.classList.add("highlight-column");
  }
};

const getSortIcon = () => {
  return sortOrder.value === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up';
};

const getCardState = (status) => {
  return status === 'active' ? 'Active' : 'Disabled';
};

// Open chatter drawer
const openChatter = (rowData) => {
  const responsibilityCode = rowData.data?.code || rowData.info?.code || '';
  const responsibilityName = rowData.data?.name || '';
  
  listData.value = {
    id: rowData.realId || rowData.id,
    responsibility_code: responsibilityCode,
    responsibility_name: responsibilityName,
    status: rowData.status,
  };
  typeValue.value = responsibilityName;
  typeName.value = "responsibility";
  iconName.value = "org_icon_responsibility";
  drawer.value = true;
  emit("openChatterDrawer", listData.value);
};

// Close chatter drawer
const closeDrawer = () => {
  drawer.value = false;
};

// Map status to the format expected by ResponsibilityLineMenu
const getResponsibilityStatus = (rowData) => {
  const status = rowData.status?.toLowerCase();
  const isArchived = rowData.archived || false;
  
  if (isArchived) return 'Archived';
  if (status === 'draft') return 'Draft';
  if (status === 'active') return 'Active';
  if (status === 'retired') return 'Retired';
  return 'Draft'; // Default fallback
};

// Get responsibility name safely
const getResponsibilityName = (rowData) => {
  return (rowData.data && rowData.data.name) ? rowData.data.name : '';
};

const emit = defineEmits(['optionClick', 'onButtonAction', 'onSearch', 'onFilter', 'removeFilter', 'checkedItemsChanged', 'page-change', 'detach', 'drilldown', 'openChatterDrawer', 'requestMoreCheckpointsChatter', 'responsibility-action']);

// Handle actions from ResponsibilityLineMenu
const handleResponsibilityAction = (actionType, rowData) => {
  // Map action types to match the expected format for optionClick
  const actionMap = {
    'activate': 'activate_action',
    'delete': 'delete_action',
    'retire': 'retire_action',
    'reactivate': 'reactivate_action',
    'rename': 'rename_action',
    'archive': 'archive_action',
    'unarchive': 'unarchive_action',
    'attach_to_role': 'attach_role',
  };
  
  const mappedAction = actionMap[actionType] || actionType;
  
  // Emit both the specific responsibility-action event and the generic optionClick for backward compatibility
  emit('responsibility-action', { action: actionType, data: rowData });
  emit('optionClick', { item: { value: mappedAction }, data: rowData });
};

const emitCorrectOnClick = (onClickType) => {
  if (onClickType === "add") {
    emit("onButtonAction");
  } else if (onClickType === "filter") {
    emit("onFilter");
    showFilterDialog.value = true;
  }
};

// Debounced search function with 300ms delay
const debouncedEmitSearch = debounce((searchText) => {
  emit("onSearch", searchText);
}, 300);

const handleSearch = (searchText) => {
  debouncedEmitSearch(searchText);
};

const removeFilter = (removedFilter) => {
  clearSelection.value = removedFilter;
  emit("removeFilter", removedFilter);
};

const filterResponsibilities = (filters) => {
  showFilterDialog.value = false;
  emit("checkedItemsChanged", filters);
};

const handlePaginationRequest = (paginationData) => {
  emit('page-change', paginationData.page);
};

// Open attachment modal
const openAttachmentModal = async (rowData) => {
  // Only open if there are attachments
  if (!rowData.count_of_attachments_to_roles || rowData.count_of_attachments_to_roles === 0) {
    return;
  }

  try {
    const responsibilityId = rowData.realId || rowData.id;
    const accountId = rowData.data?.account || rowData.info?.account;
    
    if (!accountId || !responsibilityId) {
      console.error('Missing account or responsibility ID');
      return;
    }
    proxy.$loader.show();

    const attachmentsData = await getResponsibilityAttachments(accountId, responsibilityId);

    // Build code string
    const code_str = rowData.info?.code_str || rowData.data?.code_str || "";
    const codeMatch = code_str.match(/#?\d+/);
    const objectCode = codeMatch ? (codeMatch[0].startsWith('#') ? codeMatch[0] : '#' + codeMatch[0]) : '#' + (rowData.data?.code || rowData.id);

    objectValues.value = {
      objectId: responsibilityId,
      objectType: 'Responsibility',
      objectCode: objectCode,
      objectName: rowData.data?.name || '',
    };

    // Transform attachment items to match DetachOrgObjectsDialog structure
    attachItems.value = (attachmentsData?.data?.results || []).map(item => ({
      id: item.id,
      type: item.type || "role",
      code: true,
      codeValue: item.code ? `#${item.code}` : `#${item.id}`,
      name: item.name
    }));
    showAttachments.value = true;
    
    // Hide loader after a short delay
    setTimeout(() => {
      proxy.$loader.hide();
    }, 300);
  } catch (error) {
    console.error('Error fetching responsibility attachments:', error);
    proxy.$loader.hide();
  }
};

// Handle detach action
const detachItems = (item) => {
  const detachData = {
    target: objectValues.value,
    detachObj: item,
    callBackFunc: closeAttachmentDialog
  };
  emit('detach', detachData);
};

// Close attachment dialog
const closeAttachmentDialog = () => {
  showAttachments.value = false;
};

// Navigate to responsibility detail using router
const navigateToResponsibility = (resId, tab = 'purpose', filter = null) => {
  if (!router) return;
  
  const query = filter ? { resTab: tab, filter } : { resTab: tab };
  
  router.push({
    name: 'ResponsibilityDetailChild',
    params: { resId },
    query
  }).catch(() => {});
};

// Handle OrgObjectCard click - drill down to Purpose tab
const handleResponsibilityClick = (rowData) => {
  const resId = rowData.realId || rowData.id;
  navigateToResponsibility(resId, 'purpose');
};

// Handle Checklist click - drill down to Checklist tab
const handleChecklistClick = (rowData) => {
  const resId = rowData.realId || rowData.id;
  navigateToResponsibility(resId, 'checklist');
};

// Handle Assignments click - drill down to Assignments tab
const handleAssignmentsClick = (rowData) => {
  const resId = rowData.realId || rowData.id;
  navigateToResponsibility(resId, 'assignments');
};

// Handle Coaches click - drill down to Badge Holders tab with coach filter
const handleCoachesClick = (rowData) => {
  const resId = rowData.realId || rowData.id;
  navigateToResponsibility(resId, 'badge-holders', 'coach');
};

// Handle Masters click - drill down to Badge Holders tab with master filter
const handleMastersClick = (rowData) => {
  const resId = rowData.realId || rowData.id;
  navigateToResponsibility(resId, 'badge-holders', 'master');
};
</script>

<style scoped>
.organization-responsibilities-table {
  width: 100%;
  border-radius: 8px;
}

.organization-responsibilities-table :deep(.dx-treelist) {
  width: 100% !important;
}

.organization-responsibilities-table :deep(.dx-treelist-borders > .dx-treelist-headers),
.organization-responsibilities-table :deep(.dx-treelist-borders > .dx-treelist-rowsview),
.organization-responsibilities-table :deep(.dx-treelist-borders > .dx-treelist-total-footer) {
  border: none !important;
}

.organization-responsibilities-table :deep(.dx-treelist-headers .dx-treelist-table .dx-row > td) {
  background-color: #f9fafb;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  font-family: "Inter", sans-serif;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 8px;
  color: #667085;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle !important;
}

.organization-responsibilities-table :deep(.dx-treelist-cell-expandable) {
  padding-top: 20px !important;
  padding-bottom: 20px !important;
  border-bottom: 1px solid #eaecf0;
  border-right: none !important;
}

.organization-responsibilities-table :deep(.dx-treelist .dx-column-lines > td) {
  border-left: transparent !important;
  border-right: transparent !important;
  border-bottom: 1px solid #eaecf0 !important;
}

.organization-responsibilities-table .sortable-header-container {
  position: relative;
}

.organization-responsibilities-table .sortable-header-content {
  display: flex;
  align-items: center;
}
.organization-responsibilities-table .hide-left-border {
  border-left: none !important;
}

.organization-responsibilities-table .center-vertical {
  vertical-align: middle !important;
}

.organization-responsibilities-table :deep(.dx-treelist-headers .dx-header-row td) {
  cursor: pointer;
  user-select: none;
}

.organization-responsibilities-table :deep(td[role="columnheader"]) {
  vertical-align: middle !important;
  border-left: none !important;
  border-right: none !important;
}

.org-object-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  width: 100%;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.status-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  margin-top: 32px;
}

.assignments-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  margin-top: 16px;
}

.people-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  margin-top: 20px !important;
}

.masters-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  margin-top: 22px !important;
}

.no-data-message {
  text-align: center;
  padding: 40px;
  color: #667085;
  font-size: 14px;
}

.attachments-counter-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  margin-top: 22px;
  padding: 6px;
  border-radius: 8px;
  cursor: pointer;
}

.checkpoints-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  margin-top: 10px;
}

/* Chatter icon clickable styles */
.chatter-clickable {
  padding: 6px;
  text-align: center;
  margin: 5px;
  margin-top: 24px;
  width: fit-content;
  cursor: url("../assets/cursorArrow.svg"), auto;
}

.chatter-clickable:hover {
  background-color: #f2edff;
  padding: 6px;
  text-align: center;
  margin: 5px;
  margin-top: 24px;
  box-shadow: 0px 4px 4px 0px #0000000f;
  border-radius: 8px;
}

.menu-cell-wrapper {
  margin-top: 30px;
}

.hide-left-border {
  border-left: none !important;
}

.center-vertical {
  vertical-align: middle !important;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.organization-responsibilities-table :deep(.dx-treelist-cell-expandable:last-child) {
  text-align: center !important;
}

.icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.icon-svg {
  display: block;
}

::v-deep(.org-object-card-compass) {
    max-width: none !important;
    width: 100%;
}

.organization-responsibilities-table ::v-deep(.dx-treelist-headers .dx-treelist-table .dx-row > td:nth-child(1)) {
  text-align: left !important;
}

.organization-responsibilities-table ::v-deep(.dx-treelist-headers .dx-treelist-table .dx-row > td:nth-child(5)),
.organization-responsibilities-table ::v-deep(.dx-treelist-headers .dx-treelist-table .dx-row > td:nth-child(6)),
.organization-responsibilities-table ::v-deep(.dx-treelist-headers .dx-treelist-table .dx-row > td:nth-child(7)) {
  text-align: center !important;
}

/* Highlighted row styles */
.organization-responsibilities-table :deep(.org-object-tree-highlighted-row) {
  background: url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 100 8%22 preserveAspectRatio=%22none%22><rect x=%222%22 y=%221%22 width=%2295%22 height=%226%22 rx=%221%22 ry=%221%22 fill=%22%23FFF7AC%22 /></svg>') no-repeat center center;
  background-size: 100% 100%;
}

.organization-responsibilities-table :deep(.org-object-tree-normal-row-background) {
  background: #f9fafb;
}
::v-deep(.search-add-btn) {
  width: auto !important;
  max-width: none !important;
  flex-shrink: 0 !important;
}
::v-deep(.filter-input-search) {
  width: 100% !important;
  max-width: 100% !important;
  float: none !important;
  flex: 1 1 auto !important;
}
::v-deep .highlight-column .dx-treelist-text-content {
  width: 100% !important;
}
::v-deep .dx-treelist-content .dx-treelist-table .dx-row > td, .dx-treelist-content .dx-treelist-table .dx-row > tr > td {
  vertical-align: middle !important;
}
</style>